import { Card } from '../ui/Card';
import { Building2, MapPin, Phone, Mail, Globe, Briefcase } from 'lucide-react';
import type { Organization } from '../../hooks/useOrganization';

interface OrganizationSummaryProps {
  organization: Organization;
}

export function OrganizationSummary({ organization }: OrganizationSummaryProps) {
  return (
    <Card title="Informations de l'entreprise" icon={Building2}>
      <div className="space-y-6">
        {/* Informations principales de l'entreprise */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Nom de l'entreprise</p>
            <p className="font-medium">{organization.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Forme juridique</p>
            <p className="font-medium">{organization.legalForm || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">RCCM</p>
            <p className="font-medium">{organization.registrationNumber || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Numéro d'Impôt</p>
            <p className="font-medium">{organization.taxId || 'N/A'}</p>
          </div>
        </div>

        {/* Informations sur l'entrepreneur et associés */}
        <div className="pt-4 border-t">
          <h3 className="text-sm font-semibold mb-3">Direction de l'entreprise</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nom de l'entrepreneur</p>
              <p className="font-medium">{organization.entrepreneurName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Téléphone entrepreneur</p>
              <p className="font-medium">{organization.entrepreneurPhone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">E-mail entrepreneur</p>
              <p className="font-medium">{organization.entrepreneurEmail || 'N/A'}</p>
            </div>
          </div>
          
          {organization.associates && (
            <div className="mt-3">
              <p className="text-sm text-gray-500">Autres associés</p>
              <p className="font-medium">{organization.associates}</p>
            </div>
          )}
        </div>

        {/* Numéros d'affiliations */}
        <div className="pt-4 border-t">
          <h3 className="text-sm font-semibold mb-3">Numéros d'affiliations</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">CNSS</p>
              <p className="font-medium">{organization.cnssNumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">INPP</p>
              <p className="font-medium">{organization.inppNumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">ONEM</p>
              <p className="font-medium">{organization.onemNumber || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Coordonnées de l'entreprise */}
        <div className="pt-4 border-t">
          <h3 className="text-sm font-semibold mb-3">Coordonnées et activité</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <MapPin className="h-5 w-5 text-gray-400 mr-2" />
              <span>
                {organization.address ? `${organization.address}, ` : ''}
                {organization.city || 'N/A'}, {organization.country || 'N/A'}
              </span>
            </div>
            
            <div className="flex items-center">
              <Phone className="h-5 w-5 text-gray-400 mr-2" />
              <span>Téléphone entreprise: {organization.phone || 'N/A'}</span>
            </div>
            
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-gray-400 mr-2" />
              <span>E-mail entreprise: {organization.email || 'N/A'}</span>
            </div>
            
            <div className="flex items-center">
              <Globe className="h-5 w-5 text-gray-400 mr-2" />
              {organization.website ? (
                <a 
                  href={`https://${organization.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-hover"
                >
                  {organization.website}
                </a>
              ) : (
                <span>N/A</span>
              )}
            </div>
            
            <div className="flex items-center">
              <Briefcase className="h-5 w-5 text-gray-400 mr-2" />
              <span>Secteur d'activité: {organization.industry || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Informations financières */}
        <div className="pt-4 border-t">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Capital social</p>
              <p className="font-medium">
                {organization.capital ? `${parseInt(organization.capital).toLocaleString('fr-FR')} ${organization.currency || 'CDF'}` : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}